import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import type { CompanySuggestionsView } from '@galapagos/shared';
import { useSignupApi } from './useSignupApi';

/** Production starts searching at three letters. */
export const EMPLOYER_SEARCH_MIN_LENGTH = 3;
export const EMPLOYER_SEARCH_DEBOUNCE_MS = 250;

/**
 * The cache key of one directory query, so a caller can tell which query a
 * result answers. `scope` is the parent company of a carrier search, or null.
 */
export function employerSearchQueryKey(
  scenario: string | null,
  scope: string | null,
  query: string,
) {
  return ['signup', 'employers', scenario, scope, query] as const;
}

/**
 * The company directory typeahead, through the signup data seam. The text is
 * trimmed and debounced, and nothing is asked below three letters. No retry:
 * a failed lookup shows its message, and the next keystroke asks again.
 * With a `parentRef`, it lists only that parent's sub-employers. While the next
 * list loads, the previous one stays on screen only if it had the same scope.
 * Directory queries are never logged or sent to analytics (ADR-112).
 */
export function useEmployerSearch(
  text: string,
  parentRef?: string,
): UseQueryResult<CompanySuggestionsView> {
  const api = useSignupApi();
  const scenario = useSearchParams().get('scenario');
  const trimmed = text.trim();
  const [query, setQuery] = useState(trimmed);
  useEffect(() => {
    const timer = setTimeout(() => setQuery(trimmed), EMPLOYER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmed]);
  const enabled = query.length >= EMPLOYER_SEARCH_MIN_LENGTH;
  const scope = parentRef ?? null;
  return useQuery({
    queryKey: employerSearchQueryKey(scenario, scope, query),
    queryFn: () =>
      parentRef === undefined
        ? api.searchEmployers(query)
        : api.searchEmployers(query, { parentRef }),
    enabled,
    retry: false,
    placeholderData: (previous, previousQuery) =>
      enabled && previousQuery?.queryKey[3] === scope ? previous : undefined,
  });
}
